import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { LinkText } from "../../components/Core/AppText/AppText";
import { CreateButton } from "../../components/Core/Button/Button";
import { CustomerAccountAutoComplete } from "../../components/Core/UserAutoComplete/UserAutoComplete";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import NotificationButtons from "../../components/Shared/NotificationButtons/NotificationButtons";
import { ICON_TYPE } from "../../configs/constants/IconsTypes";
import { routes } from "../../configs/constants/routes";
import Customer from "../../models/Customer";
import CustomerUser, { UpdateCustomerUserRequest } from "../../models/CustomerUser";
import User from "../../models/User";
import { NotificationSettings } from "../../models/UserCustomers";
import {
	useCustomerUsersByCustomer,
	useDeleteCustomerUser,
	useUpdateCustomerUser,
} from "../../networking/customer.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { formatRoute } from "../../utils/route.utils";
import { useConfirmModalStore } from "../../stores/confirmModal.store";
import { toRelationPayload } from "../../utils/customerUser.utils";

const CustomerUsersTab: React.FC<{ customer: Customer }> = ({ customer }) => {
	const modalStore = useConfirmModalStore();
	const [selectedCustomerAccount, setSelectedCustomerAccount] = useState<User | null>(null);

	const { customerUsers } = useCustomerUsersByCustomer(
		{
			customerId: customer?.id || "",
			sort: "firstName",
		},
		!!customer
	);

	const { mutate: updateCustomerUser } = useUpdateCustomerUser();
	const { mutate: deleteCustomerUser } = useDeleteCustomerUser();

	const navigate = useNavigate();

	const { t } = useTranslation();

	const removeUser = async (userId: string) => {
		const userToRemove = customerUsers?.find((u) => u.id === userId);

		if (userToRemove && customer) {
			if (userToRemove.profile?.customers && userToRemove.profile.customers.length === 1) {
				// Delete customer if he/she belongs to only this
				await deleteCustomerUser(userId);
			} else if (userToRemove.profile?.customers) {
				// Otherwise keep the user but remove access to this customer
				await updateCustomerUser({
					userId,
					update: {
						profile: {
							customers: toRelationPayload(
								userToRemove.profile.customers.filter((c) => c.id !== customer.id)
							),
						},
					},
				});
			}
		}
	};

	const onClickRemove = (e: any, id: string) => {
		e.stopPropagation();

		const customerToDelete = (customerUsers || []).find((u) => u.id === id);

		let modal: ConfirmModalProps;

		const userName = customerToDelete?.firstName + " " + customerToDelete?.lastName;

		if ((customerToDelete?.profile?.customers?.length || 0) > 1) {
			modal = {
				title: "customer.user.removeAccess.title",
				text: t("customer.user.removeAccess.text", {
					name: userName,
					customer: customer.name,
				}),
				acceptButtonText: "customer.user.removeAccess.remove",
				rejectButtonText: "CANCEL",
				onReject: () => {
					navigateToUsersList();
				},
				onAccept: () => removeUser(id),
			};
		} else {
			modal = {
				title: "customer.user.removeUser.title",
				text: t("customer.user.removeUser.text", { name: userName }),
				acceptButtonText: "customer.user.removeUser.remove",
				rejectButtonText: "CANCEL",
				onReject: () => {
					navigateToUsersList();
				},
				onAccept: () => removeUser(id),
			};
		}
		modalStore.newModal(modal);
	};

	const navigateToUsersList = () => {
		const routePath = formatRoute([routes.CUSTOMER_DETAILS, routes.CUSTOMER_USERS].join("/"), [
			customer?.id,
		]);
		navigate(routePath);
	};

	const navigateToCreateUser = () => {
		const routePath = formatRoute(routes.CUSTOMER_USER_CREATE, [customer?.id]);
		navigate(routePath);
	};

	const navigateToEditUser = (id?: string) => {
		const routePath = formatRoute(routes.CUSTOMER_USER_EDIT, [customer?.id, id]);
		// The flag tells the edit form it was reached from this customer's page,
		// so its breadcrumb can keep the customer context (entry points like
		// global search link via an arbitrary customer and get a generic crumb).
		navigate(routePath, { state: { fromCustomer: true } });
	};

	const handleNotificationUpdate = async (
		user: CustomerUser,
		notifications: NotificationSettings
	) => {
		if (customer && user?.id && user.profile?.customers) {
			const userData: UpdateCustomerUserRequest = {
				profile: {
					customers: toRelationPayload(user.profile.customers).map((relation) =>
						relation.id === customer.id
							? { ...relation, notificationSetting: notifications }
							: relation
					),
				},
			};
			await updateCustomerUser({ userId: user.id, update: userData });
		}
	};

	function handleSelectCustomerToInvite(user: User | null) {
		if (user) {
			const addUserModal: ConfirmModalProps = {
				title: "customers.user.confirmAdd.title",
				text: "customers.user.confirmAdd.text",
				acceptButtonText: "YES",
				rejectButtonText: "NO",
				onAccept: () => {
					const customerUser = user as CustomerUser;
					const isAlreadyAdded = !!(customerUser.profile?.customers || []).find(
						(c) => c.id === customer?.id
					);
					if (!isAlreadyAdded) {
						addUserToCustomer(user as CustomerUser);
					}
					setSelectedCustomerAccount(user);
				},
				onReject: () => {
					setSelectedCustomerAccount(user);
				},
				type: "SUCCESS",
			};
			modalStore.newModal(addUserModal);
		} else {
			setSelectedCustomerAccount(null);
		}
	}

	async function addUserToCustomer(user: Partial<CustomerUser>) {
		if (customer) {
			const userData: UpdateCustomerUserRequest = {
				profile: {
					customers: [
						...toRelationPayload(user.profile?.customers || []),
						{
							id: customer.id,
							notificationSetting: {
								jobConfirmation: true,
								jobReport: true,
								timeReportSummery: true,
							},
						},
					],
				},
			};
			await updateCustomerUser({ userId: user.id!, update: userData });
		}
	}

	return (
		<div>
			<Flex justifyContent="space-between" margin="1.5rem 0">
				<CustomerAccountAutoComplete
					value={selectedCustomerAccount}
					onChange={handleSelectCustomerToInvite}
				/>
				<CreateButton onClick={() => navigateToCreateUser()}>{t("CREATE_USER")}</CreateButton>
			</Flex>

			{customerUsers && customerUsers.length === 0 && <EmptyState label="customers.user.noUsers" />}

			{customerUsers && customerUsers.length > 0 && (
				<TableLayout>
					<table>
						<thead>
							<tr>
								<th style={{ width: 175 }}>{t("FIRST_NAME")}</th>
								<th style={{ width: 200 }}>{t("LAST_NAME")}</th>
								<th style={{ width: 200 }}>{t("TITLE")}</th>
								<th>{t("PHONE")}</th>
								<th>{t("NOTIFICATIONS")}</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{customerUsers?.map((user) => {
								const currentUserCustomer = (user.profile?.customers || []).find(
									(c) => c.id === customer?.id
								);
								return (
									<tr key={user.id} data-contact={user.id}>
										<td onClick={() => navigateToEditUser(user.id)}>{user.firstName || "-"}</td>
										<td onClick={() => navigateToEditUser(user.id)}>{user.lastName || "-"}</td>
										<td onClick={() => navigateToEditUser(user.id)}>
											{user?.profile?.title || "-"}
										</td>
										<td>
											{user?.profile?.phoneNumber && (
												<a href={`tel:${user.profile.phoneNumber}`}>
													<LinkText as="span">{user.profile.phoneNumber}</LinkText>
												</a>
											)}
										</td>
										<td style={{ width: 150 }} onClick={() => navigateToEditUser(user.id)}>
											<NotificationButtons
												notifications={currentUserCustomer?.notificationSetting}
												editable
												onNotificationUpdate={(e: NotificationSettings) =>
													handleNotificationUpdate(user, e)
												}
											/>
										</td>
										<td onClick={() => navigateToEditUser(user.id)}>
											<ImageContainer>
												<ImageWrapper
													src={ICON_TYPE.CROSS}
													onClick={(e) => onClickRemove(e, user.id)}
												/>
											</ImageContainer>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</TableLayout>
			)}
		</div>
	);
};

export default CustomerUsersTab;

const ImageContainer = styled.div`
	margin-right: 1.8rem;
	margin-left: 12.5rem;
	display: flex;
	justify-content: flex-end;
`;
const ImageWrapper = styled.img`
	padding: 8px;
	/* margin-top: 1.25rem; */
`;
